import type { Task } from 'fhir/r4';
import moment from 'moment';

const ENROLLMENT_ACTION_CODE = 'enrollment-action';
const TASK_CODES_SYSTEM = 'https://projectwell.io/fhir/codes/task-codes';
const ENROLLMENT_START_WEEK_URL = 'https://projectwell.io/fhir/extensions/enrollment-start-week';
const ACTIVE_ENROLLMENT_STATUSES = new Set(['in-progress', 'completed', 'on-hold']);

interface TaskBundleEntry {
  resource: Task;
}

export interface EnrollmentProgramDates {
  /** Backdated anchor used for week/day math (week 1 day 1). */
  programStartDate: string;
  /** Effective enrollment launch date (`executionPeriod.start`, not after today). */
  launchDate: string;
  /** Raw executionPeriod.start from FHIR before future-date clamp. */
  rawLaunchDate: string;
  enrollmentStartWeek: number;
}

function isEnrollmentActionTask(task: Task): boolean {
  return (
    task.code?.coding?.some(
      (coding) => coding.code === ENROLLMENT_ACTION_CODE && (!coding.system || coding.system === TASK_CODES_SYSTEM)
    ) ?? false
  );
}

/** Reads the enrollment-start-week extension, defaulting to week 1. */
export function getEnrollmentStartWeek(task: Task): number {
  const extension = task.extension?.find((item) => item.url === ENROLLMENT_START_WEEK_URL);
  const week = extension?.valueInteger;
  return typeof week === 'number' && Number.isFinite(week) && week > 0 ? week : 1;
}

function getTaskLaunchDate(task: Task): string | null {
  const launchDate = task.executionPeriod?.start;
  // Parse and format as UTC calendar dates so launch matches week math (also UTC).
  if (!launchDate || !moment.utc(launchDate, moment.ISO_8601, true).isValid()) {
    return null;
  }
  return moment.utc(launchDate, moment.ISO_8601).format('YYYY-MM-DD');
}

/** Today in UTC as `YYYY-MM-DD` (date-only counters ignore wall-clock time of day). */
export function getUtcToday(): string {
  return moment.utc().format('YYYY-MM-DD');
}

/**
 * Clamps a calendar date so a future `executionPeriod.start` cannot push program week negative.
 * Active enrollments with a future start still show as week 1 once the member has been launched.
 *
 * @param dateStr - `YYYY-MM-DD` calendar date.
 * @returns The earlier of `dateStr` and today.
 */
export function clampDateToTodayOrPast(dateStr: string): string {
  const today = getUtcToday();
  if (!moment.utc(dateStr, 'YYYY-MM-DD', true).isValid()) {
    return dateStr;
  }
  return dateStr > today ? today : dateStr;
}

/**
 * Computes program week from an anchor date and optional on-hold day count.
 * Never returns less than 1 when the anchor is a valid past/today date (or was clamped).
 *
 * @param programStartDate - Week 1 day 1 (`YYYY-MM-DD`).
 * @param totalOnHoldDays - Full calendar days previously spent on hold (non-negative).
 * @returns Whole program week number (1-based).
 */
export function calculateProgramWeekNumber(programStartDate: string, totalOnHoldDays = 0): number {
  const start = moment.utc(programStartDate, 'YYYY-MM-DD', true);
  if (!start.isValid()) {
    return 1;
  }

  const startDay = start.clone().startOf('day');
  const today = moment.utc().startOf('day');
  // If the anchor is still in the future (bad data), treat as day 0 of week 1.
  const totalElapsedDays = Math.max(0, today.diff(startDay, 'days') + 1);
  const holdDays = Math.max(0, totalOnHoldDays);
  // On-hold days must never exceed elapsed program time (otherwise week goes negative).
  const totalActiveDays = Math.max(0, totalElapsedDays - Math.min(holdDays, totalElapsedDays));
  return Math.floor(totalActiveDays / 7) + 1;
}

/**
 * Resolves program-week dates from the patient's enrollment-action task.
 *
 * @param tasks - FHIR Task bundle entries for the patient.
 * @param referralId - When provided, prefer the task for the active referral.
 */
export function resolveEnrollmentProgramDates(
  tasks: TaskBundleEntry[] | undefined,
  referralId?: string | false
): EnrollmentProgramDates | null {
  if (!tasks?.length) {
    return null;
  }

  const enrollmentTasks = tasks
    .map((entry) => entry.resource)
    .filter((task) => {
      if (!isEnrollmentActionTask(task) || !ACTIVE_ENROLLMENT_STATUSES.has(task.status)) {
        return false;
      }
      return Boolean(getTaskLaunchDate(task));
    })
    .sort(
      (a, b) =>
        moment.utc(getTaskLaunchDate(b), 'YYYY-MM-DD').valueOf() -
        moment.utc(getTaskLaunchDate(a), 'YYYY-MM-DD').valueOf()
    );

  if (!enrollmentTasks.length) {
    return null;
  }

  // `[0]` is `Task | undefined` only under `noUncheckedIndexedAccess`; the
  // `if (!enrollmentTasks.length) return null` three lines above proves it is present.
  let selectedTask = enrollmentTasks[0]!;
  if (referralId && typeof referralId === 'string') {
    const referralMatch = enrollmentTasks.find((task) => task.groupIdentifier?.value === referralId);
    if (referralMatch) {
      selectedTask = referralMatch;
    }
  }

  const rawLaunchDate = getTaskLaunchDate(selectedTask);
  if (!rawLaunchDate) {
    return null;
  }

  // Active EA with a future executionPeriod.start produces negative weeks (e.g. -9).
  // Use today as the effective launch for week math so "launched" members start at week 1.
  const launchDate = clampDateToTodayOrPast(rawLaunchDate);
  const enrollmentStartWeek = getEnrollmentStartWeek(selectedTask);
  const programStartDate = moment
    .utc(launchDate, 'YYYY-MM-DD')
    .subtract(enrollmentStartWeek - 1, 'weeks')
    .format('YYYY-MM-DD');

  return {
    programStartDate,
    launchDate,
    rawLaunchDate,
    enrollmentStartWeek,
  };
}
